import { useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';

/*
  A person's theme and light or dark mode: which one is showing, and saving it.

  Theme system, phase 4, 14 September 2026; docs/THEME-SYSTEM.md sections 2
  and 3, and the implementation brief's phase 4.

  - **Stored on the person's profile**, `profiles.theme` and
    `profiles.theme_mode` (migration 20260914210000), so it follows them across
    devices AND companies. Not user_preferences, which is per company.
  - **Resolution**: their saved theme, else Plain. Mode: their saved mode, and
    when that is Automatic, the device's own light or dark setting, followed
    live if the device changes while OnHand is open.
  - **Applied as `data-theme` and `data-mode` on <html>.** Everything else is
    CSS: public/theme/themes.css and src/theme/roles.css. `data-mode` is always
    the RESOLVED mode, light or dark, because that is what the stylesheets read.
  - **No flash.** Nothing is set until the profile is known: the shared frame
    only mounts once it is, and applies this before the first paint. On the way
    out (signing out) both attributes are removed, so the sign-in screen, which
    reads no theme, never shows the last person's.
  - **`data-color-mode` too, from rebrand step 1** (17 September 2026): the
    OnHand brand's tokens (src/styles/brand-tokens.css) read that name for light
    or dark. It always holds exactly what `data-mode` holds, set and removed at
    the same moment, so how the mode is chosen does not change.
  - **Saving is optimistic with a roll back**, matching src/lib/preferences.js:
    the look changes at once, the row is written and READ BACK, and if what came
    back is not what was sent the look goes back to what was saved. A save is
    proved by reading the row back, never by the absence of an error.
*/

/*
  **The brand's ten themes**, rebrand step 6, 17 September 2026, in the brand
  guide's 8.3 order. They replace the fourteen of the theme system on screen.
  `docs/brand/reference/onhand-themes-approved-v1.html` is what each one looks
  like; the colours are in `src/styles/brand-tokens.css` and the mapping onto
  this app's colour roles is `src/theme/brandThemes.css`.
*/
export const THEMES = [
  { slug: 'open',      name: 'Open Stock' },
  { slug: 'shelves',   name: 'Warehouse' },
  { slug: 'blueprint', name: 'Blueprint' },
  { slug: 'routes',    name: 'Routes' },
  { slug: 'barcode',   name: 'Barcode' },
  { slug: 'topo',      name: 'Topography' },
  { slug: 'horizon',   name: 'Horizon' },
  { slug: 'pattern',   name: 'Pattern' },
  { slug: 'soft',      name: 'Soft Focus' },
  { slug: 'plain',     name: 'Plain' },
];
export const MODES = ['auto', 'light', 'dark'];
/*
  **Everyone starts on Plain**, the owner's word of 17 September 2026, asked
  before step 6 was built: a theme saved under the old fourteen, including the
  twelve people still on the value nobody chose, shows Plain until the person
  picks one of the ten. Nothing saved is changed or deleted; the old value stays
  on the profile until they choose again, and the database still allows it.
*/
export const DEFAULT_THEME = 'plain';
export const DEFAULT_MODE = 'auto';

/* Kept for anything that asks; `validTheme` below is what the app uses. */
export const BRAND_THEMES = THEMES.map(t => t.slug);
export const BRAND_DEFAULT_THEME = DEFAULT_THEME;
export const brandThemeFor = saved => (BRAND_THEMES.includes(saved) ? saved : BRAND_DEFAULT_THEME);

const SLUGS = new Set(THEMES.map(t => t.slug));
export const validTheme = t => (SLUGS.has(t) ? t : DEFAULT_THEME);
export const validMode = m => (MODES.includes(m) ? m : DEFAULT_MODE);

const DARK_QUERY = '(prefers-color-scheme: dark)';
function deviceIsDark() {
  try { return !!(window.matchMedia && window.matchMedia(DARK_QUERY).matches); } catch { return false; }
}

/* Automatic follows the device; a saved Light or Dark sticks whatever it says. */
export function resolveMode(mode, dark = deviceIsDark()) {
  const m = validMode(mode);
  if (m === 'light' || m === 'dark') return m;
  return dark ? 'dark' : 'light';
}

// ---- The one piece of state, shared by the frame and Settings ---------------
let state = { known: false, userId: null, theme: DEFAULT_THEME, mode: DEFAULT_MODE, deviceDark: false };
const listeners = new Set();
const emit = () => listeners.forEach(fn => fn());
const setState = patch => { state = { ...state, ...patch }; paint(); emit(); };

function paint() {
  if (typeof document === 'undefined') return;
  const d = document.documentElement.dataset;
  if (!state.known) { delete d.theme; delete d.mode; delete d.colorMode; return; }
  d.theme = state.theme;
  d.mode = resolveMode(state.mode, state.deviceDark);
  d.colorMode = d.mode;
}

let watching = false;
function watchDevice() {
  if (watching || typeof window === 'undefined' || !window.matchMedia) return;
  watching = true;
  try {
    const q = window.matchMedia(DARK_QUERY);
    const sync = () => setState({ deviceDark: q.matches });
    if (q.addEventListener) q.addEventListener('change', sync);
    else if (q.addListener) q.addListener(sync);
  } catch { /* a browser that cannot answer keeps light for Automatic */ }
}

/* Saves still on their way. A profile read that lands mid-save must not put
   the old theme back over the one the person just chose. */
let pending = 0;
let lastSave = 0;

/* The signed-in person's saved choice becomes the look. */
export function adoptProfile(profile) {
  watchDevice();
  if (!profile) { clearAppearance(); return; }
  if (pending > 0 && state.userId === profile.id) return;
  setState({
    known: true,
    userId: profile.id,
    theme: validTheme(profile.theme),
    mode: validMode(profile.theme_mode),
    deviceDark: deviceIsDark(),
  });
}

/* Nobody is signed in: no theme at all. */
export function clearAppearance() {
  setState({ known: false, userId: null, theme: DEFAULT_THEME, mode: DEFAULT_MODE });
}

/*
  Change the theme, the mode, or both. Shows it at once and resolves true only
  when the database holds exactly what was sent; otherwise the look goes back to
  what was saved before (unless a newer choice has been made since) and it
  resolves false, so the screen can say so.
*/
export async function saveAppearance({ theme, mode }) {
  if (!state.known || !state.userId) return false;
  const before = { theme: state.theme, mode: state.mode };
  const next = { theme: theme === undefined ? state.theme : validTheme(theme), mode: mode === undefined ? state.mode : validMode(mode) };
  const mine = ++lastSave;
  pending += 1;
  setState(next);
  let ok = false;
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({ theme: next.theme, theme_mode: next.mode })
      .eq('id', state.userId)
      .select('theme, theme_mode')
      .maybeSingle();
    ok = !error && !!data && data.theme === next.theme && data.theme_mode === next.mode;
  } catch {
    ok = false;
  } finally {
    pending -= 1;
  }
  if (!ok && mine === lastSave) setState(before);
  return ok;
}

export function getAppearance() { return state; }

function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/* For Settings: the saved choice, what Automatic currently resolves to, and the
   resolved mode showing. */
export function useAppearance() {
  const s = useSyncExternalStore(subscribe, getAppearance, getAppearance);
  return {
    known: s.known,
    theme: s.theme,
    mode: s.mode,
    deviceMode: s.deviceDark ? 'dark' : 'light',
    showingMode: resolveMode(s.mode, s.deviceDark),
  };
}
